import { appLocalStorage, appSessionStorage } from '../utils/storage';
import { routePath } from '../appLinks/links';
import { safeAuthReturn } from '../guestTrial/store';

export const ONBOARDING_KEY = 'guest-onboarding:v1';
const VISIT_KEY = 'guest-onboarding:skip-visit';
export const STEP_COUNT = 5;
const memory = new Map();
const unsaved = new Map();
const empty = () => ({ step: 0, completed: false });
const visitKey = scope => scope === ONBOARDING_KEY ? VISIT_KEY : `${scope}:skip-visit`;
export const accountScope = id => Number.isSafeInteger(Number(id)) && Number(id) > 0 ? `onboarding:account:${id}` : null;

function readRecord(scope) {
  let raw;
  try { raw = appLocalStorage.getItem(scope); } catch { return memory.get(scope) || empty(); }
  if (unsaved.has(scope) && unsaved.get(scope) === raw) return memory.get(scope) || empty();
  unsaved.delete(scope);
  try {
    const value = JSON.parse(raw);
    if (value && Number.isInteger(value.step) && value.step >= 0 && value.step < STEP_COUNT
      && typeof value.completed === 'boolean') memory.set(scope, value);
    else memory.delete(scope);
  } catch { memory.delete(scope); }
  return memory.get(scope) || empty();
}

function writeRecord(scope, value) {
  memory.set(scope, value);
  try { appLocalStorage.setItem(scope, JSON.stringify(value)); unsaved.delete(scope); } catch {
    // A full quota can reject writes while reads still succeed. Keep this
    // session's progress until storage changes or becomes writable again.
    try { unsaved.set(scope, appLocalStorage.getItem(scope)); } catch { /* Read fallback uses memory. */ }
  }
}

export function readProgress(scope = ONBOARDING_KEY) {
  const { step, completed } = readRecord(scope);
  return { step, completed };
}

export function hasProgress(scope = ONBOARDING_KEY) {
  readRecord(scope);
  return memory.has(scope);
}

// Only a positively identified new installation can discard completed progress.
export function resetProgress(scope) {
  writeRecord(scope, empty());
}

export function saveProgress(step, completed = false, scope = ONBOARDING_KEY) {
  if (!Number.isInteger(step) || step < 0 || step >= STEP_COUNT) return;
  const previous = readRecord(scope);
  writeRecord(scope, { ...previous, step, completed: completed || previous.completed });
}

export function requestAccountOnboarding(id, returnTo) {
  const scope = accountScope(id);
  if (!scope) return false;
  const previous = readRecord(scope);
  if (previous.completed) return false;
  writeRecord(scope, { ...previous, requested: true, returnTo: safeAuthReturn(returnTo) });
  resumeVisit(scope);
  return true;
}

export function pendingAccountOnboarding(id) {
  const scope = accountScope(id);
  if (!scope) return null;
  const record = readRecord(scope);
  return record.requested && !record.completed ? scope : null;
}

export const onboardingReturn = scope => safeAuthReturn(readRecord(scope).returnTo || '/');

export function skipVisit(scope = ONBOARDING_KEY) {
  try { appSessionStorage.setItem(visitKey(scope), '1'); } catch { /* Provider also keeps this in memory. */ }
}
export function visitSkipped(scope = ONBOARDING_KEY) {
  try { return Boolean(appSessionStorage.getItem(visitKey(scope))); } catch { return false; }
}
export function resumeVisit(scope) {
  try { appSessionStorage.removeItem(visitKey(scope)); } catch { /* Memory fallback in provider. */ }
}

export function isPlainHome(location) {
  return routePath(location.pathname) === '/' && !location.search && !location.hash;
}

export function eligibleEntry(location, referrer, authenticated = false,
  { scope = ONBOARDING_KEY, registration = false, installed = false } = {}) {
  if ((authenticated && scope === ONBOARDING_KEY) || !isPlainHome(location) || (location.state && !registration)) return false;
  return !visitSkipped(scope) && (registration || directEntry(referrer, installed)) && !readProgress(scope).completed;
}

export function directEntry(referrer, installed = false) {
  try {
    if (installed && referrer) {
      const source = new URL(referrer);
      if (source.protocol === 'android-app:' && source.host === 'today.wiw.twa') return true;
    }
    // Unknown query/hash, external referrers and explicit native links take
    // priority over teaching. A browser can omit referrer entirely.
    if (referrer && new URL(referrer).origin !== window.location.origin) return false;
  } catch { return false; }
  return true;
}
